import { Component, Event, EventEmitter, h, Method, Prop } from "@stencil/core"
import LanguageHandler from "@payair/e-com-common-sdk/core/LanguageHandler"
import { isConsumerEmailValid } from "../../helpers/ConsumerInputValidation"
import { reconcileControlledValue } from "../../helpers/reconcileControlledValue"
import { getSupportedLocaleData, getLanguageHandler } from "../../languageHandler/LanguageHandler"
import inputErrorIcon from "../../styles/inputErrorIcon.svg"

let emailInputId = 0

/** Controlled email input. */
@Component({
  tag: "email-input",
  styleUrl: "EmailInput.scss",
  shadow: true,
})
export class EmailInput {
  /**
   * Merchant site preferred locale. Format based on ISO format for language (ISO 639-1) and country code
   * (ISO 3166-1 alpha-2). The language and country should be separated using an underscore ( _ ).
   * <br /><br />
   * **Supported locale**: en_GB, sv_SE, pl_PL
   * <br /><br />
   * @default en_GB
   */
  @Prop() locale?: string

  /** Current controlled value. */
  @Prop() value: string = ""

  /** Whether the input is required. */
  @Prop() required?: boolean

  /** Whether the input is disabled. */
  @Prop() disabled?: boolean

  /** Whether invalid non-empty values display a validation message. */
  @Prop() showValidationError: boolean = true

  /** Whether the input should be rendered in its externally controlled invalid state. */
  @Prop() invalid?: boolean

  /** Translation key used for an externally controlled validation message. */
  @Prop() errorMessageKey?: string

  /** Fully translated validation message. Takes precedence over errorMessageKey. */
  @Prop() errorMessage?: string

  /** Native autocomplete hint. */
  @Prop() autocomplete?: string = "email"

  /** Translation key used for the visible label. */
  @Prop() labelKey: string = "emailAddress"

  /** Translation key used for the placeholder. Defaults to labelKey. */
  @Prop() placeholderKey?: string

  /** Emitted whenever the user edits the value. */
  @Event() valueChange: EventEmitter<string>

  /** Emitted when focus leaves the input. */
  @Event() fieldBlur: EventEmitter<void>

  /** Emitted when Enter is pressed in the input. */
  @Event() fieldSubmit: EventEmitter<void>

  private languageHandler: LanguageHandler
  private readonly inputId = `email-input-${emailInputId++}`
  private inputElement?: HTMLInputElement

  componentWillLoad() {
    const localeData = getSupportedLocaleData(this.locale)
    this.languageHandler = getLanguageHandler(localeData.lang)
  }

  private emailOnInput = (event: InputEvent) => {
    const input = event.target as HTMLInputElement
    const nextValue = input.value

    this.valueChange.emit(nextValue)

    if (!event.isComposing) {
      reconcileControlledValue(input, () => this.value || "")
    }
  }

  private emailOnCompositionEnd = (event: CompositionEvent) => {
    reconcileControlledValue(event.target as HTMLInputElement, () => this.value || "")
  }

  private onBlur = () => {
    this.fieldBlur.emit()
  }

  private onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter" && !event.isComposing && !event.defaultPrevented && !event.repeat && !this.disabled) {
      event.preventDefault()
      this.fieldSubmit.emit()
    }
  }

  /** Focuses the native email input. */
  @Method()
  async focusInput() {
    this.inputElement?.focus()
  }

  render() {
    const displayValue = this.value || ""
    const formatInvalid = this.showValidationError && displayValue.length > 0 && !isConsumerEmailValid(displayValue)
    const invalid = this.invalid === true || formatInvalid
    const errorMessageKey = this.invalid === true ? this.errorMessageKey || "requiredField" : "wrongFormat"
    const errorMessage = this.errorMessage || this.languageHandler.getString(errorMessageKey)
    const label = this.languageHandler.getString(this.labelKey)
    const placeholder = this.languageHandler.getString(this.placeholderKey || this.labelKey)

    return (
      <div class="emailInput">
        <label class="inputLabel" htmlFor={this.inputId}>
          {label}
        </label>
        <div class="inputContainer">
          <input
            ref={element => (this.inputElement = element)}
            id={this.inputId}
            class="input"
            type="email"
            autocomplete={this.autocomplete}
            placeholder={placeholder}
            onInput={this.emailOnInput}
            onCompositionend={this.emailOnCompositionEnd}
            onKeyDown={this.onKeyDown}
            onBlur={this.onBlur}
            value={displayValue}
            required={this.required}
            disabled={this.disabled}
            aria-invalid={invalid ? "true" : "false"}
            aria-describedby={invalid ? `${this.inputId}-error` : undefined}
          />
          {invalid && <img class="errorIcon" src={inputErrorIcon} alt="" aria-hidden="true" />}
        </div>
        {invalid && (
          <div id={`${this.inputId}-error`} class="errorInfo" role="alert">
            {errorMessage}
          </div>
        )}
      </div>
    )
  }
}
